import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Calendar, Users, Clock, AlertTriangle, TrendingUp, Sparkles, RefreshCw,
  ChevronRight, Cake, Award, Camera, Package, IndianRupee, ClipboardCheck,
  CheckCircle2, Flag, FileText, Briefcase, Clapperboard, ClipboardList, Target,
  AlertCircle
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import SidebarLayout from '../components/SidebarLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import API from '../utils/api';

const SEV_COLOR = {
  high:   'border-rose-500/30 bg-rose-500/5 text-rose-300',
  medium: 'border-amber-500/30 bg-amber-500/5 text-amber-300',
  low:    'border-slate-500/30 bg-slate-500/5 text-slate-400',
};

const FLAG_ICON = {
  late_regression: AlertTriangle, late_chronic: Clock, absence: AlertCircle,
  ts_backfill: FileText, no_timesheet: FileText, overdue: AlertTriangle,
  carry_forward_high: RefreshCw, low_on_time: TrendingUp, silence: AlertCircle,
};

export default function DailyBriefPage() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');

  const load = useCallback(async (dateParam) => {
    setLoading(true);
    try {
      const url = dateParam ? `${API}/daily-brief/${dateParam}` : `${API}/daily-brief/today`;
      const { data } = await axios.get(url, { withCredentials: true });
      setBrief(data);
      setSelectedDate(data.date);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load brief');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(null); }, [load]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await axios.post(`${API}/daily-brief/regenerate`, null, {
        params: { date: selectedDate },
        withCredentials: true,
      });
      setBrief(data);
      toast.success('Brief regenerated with latest data');
    } catch {
      toast.error('Regeneration failed');
    } finally { setRegenerating(false); }
  };

  if (loading && !brief) {
    return <SidebarLayout title="Daily Brief" subtitle="Morning intelligence"><div className="flex items-center justify-center h-64 text-slate-500">Building your brief...</div></SidebarLayout>;
  }
  if (!brief) return <SidebarLayout title="Daily Brief" subtitle="Morning intelligence"><div className="text-slate-500">No brief available</div></SidebarLayout>;

  const m = brief.metrics || {};
  const att = m.attendance?.counts || {};
  const ts = m.today_schedule || brief.today_schedule || {};

  return (
    <SidebarLayout title="Daily Intelligence" subtitle={`Morning brief — ${formatDate(brief.date)}`}>
      {/* Date picker + regenerate */}
      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="brief-controls">
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-slate-500" />
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => { setSelectedDate(e.target.value); load(e.target.value); }}
            className="bg-black/40 border-white/10 text-white h-8 text-xs w-40"
            data-testid="brief-date-picker"
          />
        </div>
        <span className="text-[10px] text-slate-500">· Looking at {formatDate(brief.date)}</span>
        <div className="flex-1" />
        <Button onClick={handleRegenerate} disabled={regenerating} variant="outline" className="bg-white/5 border-white/10 text-slate-300 h-8 text-xs" data-testid="regenerate-brief-btn">
          <RefreshCw size={13} className={`mr-1 ${regenerating ? 'animate-spin' : ''}`} />
          {regenerating ? 'Regenerating...' : 'Regenerate'}
        </Button>
      </div>

      {/* Narrative (AI-generated, top of page) */}
      {brief.narrative ? (
        <div className="glass-card p-6 mb-6 border border-indigo-500/20 bg-gradient-to-br from-indigo-500/5 to-transparent" data-testid="brief-narrative">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} className="text-indigo-400" />
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-indigo-300">AI Chief of Staff</span>
          </div>
          <div className="space-y-3 text-sm text-slate-300 leading-relaxed">
            <ReactMarkdown
              components={{
                h2: ({ children }) => <h3 className="text-[11px] font-bold tracking-[0.2em] uppercase text-indigo-300 mt-5 mb-2 first:mt-0">{children}</h3>,
                h3: ({ children }) => <h4 className="text-xs font-semibold text-white mt-3 mb-1">{children}</h4>,
                p: ({ children }) => <p className="text-sm text-slate-300 leading-relaxed">{children}</p>,
                ul: ({ children }) => <ul className="space-y-1.5 my-2">{children}</ul>,
                li: ({ children }) => <li className="text-sm text-slate-300 pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-indigo-400">{children}</li>,
                strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
                em: ({ children }) => <em className="text-indigo-300 not-italic font-medium">{children}</em>,
              }}
            >{brief.narrative}</ReactMarkdown>
          </div>
        </div>
      ) : (
        <div className="glass-card p-4 mb-6 border border-white/5 text-center text-xs text-slate-500" data-testid="narrative-empty">
          No AI narrative for this historical date. Click Regenerate to create one.
        </div>
      )}

      {/* ATTENTION LIST */}
      {brief.attention_list && brief.attention_list.length > 0 && (
        <section className="mb-6" data-testid="attention-list">
          <SectionHeader icon={AlertTriangle} color="text-rose-400" title="Needs Your Attention" count={brief.attention_list.length} />
          <div className="space-y-2">
            {brief.attention_list.map((a, i) => (
              <div key={i} className={`glass-card p-3 border ${SEV_COLOR[a.severity] || SEV_COLOR.low}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] px-2 py-0.5 rounded border ${SEV_COLOR[a.severity]}`}>{a.severity?.toUpperCase()}</span>
                  <span className="text-sm text-white font-medium flex-1">{a.title}</span>
                </div>
                {a.details && Array.isArray(a.details) && a.details.length > 0 && (
                  <div className="mt-2 pl-3 border-l border-white/5 space-y-1">
                    {a.details.slice(0, 5).map((d, j) => (
                      <div key={j} className="text-[11px] text-slate-400">
                        {d.name || d.title || d.invoice_number || d.msg || JSON.stringify(d).slice(0, 80)}
                        {d.check_in && <span className="text-slate-600"> · {d.check_in}</span>}
                        {d.deadline && <span className="text-slate-600"> · Due {d.deadline}</span>}
                        {d.total_amount && <span className="text-slate-600"> · ₹{d.total_amount}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* KPI grid — yesterday's snapshot */}
      <section className="mb-6" data-testid="kpi-grid">
        <SectionHeader icon={TrendingUp} color="text-indigo-400" title={`Yesterday's Snapshot — ${formatDate(brief.date)}`} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Present" value={att.Present || 0} sub={`+ ${att.WFH || 0} WFH, ${att['Out on Shoot'] || 0} shoot`} color="text-emerald-400" />
          <KpiCard label="Late / Absent" value={`${att.Late || 0} / ${att.Absent || 0}`} sub={`total ${m.attendance?.total_logs || 0} logs`} color={att.Late ? 'text-amber-400' : 'text-slate-400'} />
          <KpiCard label="Hours Logged" value={`${m.timesheet?.total_hours || 0}h`} sub={`${m.timesheet?.total_entries || 0} entries`} color="text-cyan-400" />
          <KpiCard label="Approvals" value={m.approvals?.pending || 0} sub={`${m.approvals?.flagged || 0} flagged · ${m.approvals?.cleared_yesterday || 0} cleared yday`} color="text-purple-400" />
        </div>
      </section>

      {/* Board activity */}
      <section className="mb-6">
        <SectionHeader icon={Clapperboard} color="text-cyan-400" title="Board Activity" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <BoardCard board={m.production} icon={Clapperboard} color="text-cyan-400" />
          <BoardCard board={m.execution} icon={ClipboardList} color="text-fuchsia-400" />
          <BoardCard board={m.performance} icon={Target} color="text-blue-400" />
        </div>
      </section>

      {/* Finance + Deliverables + Assets row */}
      <section className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-3">
        <FinanceCard finance={m.finance} />
        <DeliverablesCard deliverables={m.deliverables} missed={brief.attention_list?.find(a => a.type === 'overdue_deliverable')?.details || []} />
        <AssetsCard assets={m.assets} />
      </section>

      {/* Employee patterns (drill into the detail) */}
      {brief.employee_patterns && brief.employee_patterns.length > 0 && (
        <section className="mb-6" data-testid="employee-patterns">
          <SectionHeader icon={Users} color="text-amber-400" title="Behavioural Patterns (7-day rolling)" count={brief.employee_patterns.length} />
          <div className="space-y-2">
            {brief.employee_patterns.map((p, i) => (
              <EmployeePatternCard key={i} pattern={p} />
            ))}
          </div>
        </section>
      )}

      {/* Today's schedule */}
      <section className="mb-6">
        <SectionHeader icon={Calendar} color="text-emerald-400" title={`Today — ${formatDate(brief.today)}`} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SchedCard icon={Camera} color="text-rose-400" title="Shoots Today" items={ts.shoots} empty="No shoots scheduled"
            render={(s) => <span>{s.title} <span className="text-slate-600">· {s.client_name}</span> {s.start_time && <span className="text-slate-500">@ {s.start_time}</span>}</span>} />
          <SchedCard icon={FileText} color="text-cyan-400" title="Deliverables Due Today" items={ts.deliverables_due_today} empty="No deliverables due today"
            render={(d) => <span>{d.title} <span className="text-slate-600">· {d.client_name}</span> {d.assigned_name && <span className="text-slate-500">· {d.assigned_name}</span>}</span>} />
          <SchedCard icon={Cake} color="text-pink-400" title="Birthdays Today" items={ts.birthdays} empty="No birthdays today"
            render={(b) => <span>{b.name} <span className="text-slate-500">· {b.dept}</span></span>} />
          <SchedCard icon={Award} color="text-amber-400" title="Work Anniversaries" items={ts.anniversaries} empty="No anniversaries today"
            render={(a) => <span>{a.name} <span className="text-slate-500">· since {a.since}</span></span>} />
          <SchedCard icon={ClipboardCheck} color="text-indigo-400" title="Reviews Due This Week" items={ts.reviews_due_this_week} empty="No reviews this week"
            render={(r) => <span>{r.name} <span className="text-slate-500">· {r.review_date}</span></span>} />
          <SchedCard icon={Users} color="text-purple-400" title="New Joinees (Last 7 Days)" items={ts.new_joinees_last_7_days} empty="No new joinees this week"
            render={(j) => <span>{j.name} <span className="text-slate-500">· {j.role} · {j.joined}</span></span>} />
          <SchedCard icon={Calendar} color="text-orange-400" title="On Leave Today" items={ts.leaves_today} empty="No one on leave"
            render={(l) => <span>{l.employee_name} <span className="text-slate-500">· {l.leave_type}</span></span>} />
          <SchedCard icon={CheckCircle2} color="text-emerald-400" title="Returning Today" items={ts.returning_today} empty="No one returning today"
            render={(r) => <span>{r.employee_name} <span className="text-slate-500">· {r.leave_type}</span></span>} />
        </div>
      </section>

      {/* Top performers — feel-good end */}
      {(brief.top_performers?.hours?.length > 0 || brief.top_performers?.completions?.length > 0) && (
        <section className="mb-6" data-testid="top-performers">
          <SectionHeader icon={Award} color="text-emerald-400" title="Top Performers" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <PerformerList title="Most Hours Logged" items={brief.top_performers?.hours} format={(e) => `${e.hours}h`} />
            <PerformerList title="Most Completions (7d)" items={brief.top_performers?.completions} format={(e) => `${e.completions_7d} tasks · ${e.on_time_pct}% on-time`} />
          </div>
        </section>
      )}
    </SidebarLayout>
  );
}

// ---------- Subcomponents ----------

function SectionHeader({ icon: Icon, color, title, count }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon size={14} className={color} />
      <h3 className={`text-[10px] font-semibold tracking-[0.2em] uppercase ${color}`}>{title}</h3>
      {count !== undefined && <span className={`text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10 ${color}`}>{count}</span>}
    </div>
  );
}

function KpiCard({ label, value, sub, color }) {
  return (
    <div className="kpi-card border-l-4 border-white/5">
      <span className={`text-[9px] font-semibold tracking-[0.2em] uppercase ${color}`}>{label}</span>
      <div className="font-heading text-2xl font-black text-white mt-1">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function BoardCard({ board, icon: Icon, color }) {
  if (!board) return null;
  return (
    <div className="glass-card p-4 border border-white/5">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} className={color} />
        <span className={`text-[10px] font-semibold tracking-widest uppercase ${color}`}>{board.label}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Stat label="Created" value={board.created} />
        <Stat label="Completed" value={board.completed} />
        <Stat label="On-time" value={`${board.on_time_pct}%`} />
        <Stat label="Overdue open" value={board.overdue_open} color={board.overdue_open ? 'text-rose-400' : undefined} />
      </div>
      {board.carry_forwards > 0 && (
        <div className="mt-2 text-[10px] text-amber-400">↪ {board.carry_forwards} carried forward</div>
      )}
      {board.by_employee?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/5">
          <p className="text-[9px] uppercase tracking-wider text-slate-600 mb-1">By person</p>
          <div className="space-y-0.5">
            {board.by_employee.slice(0, 5).map((e, i) => (
              <div key={i} className="flex items-center justify-between text-[11px]">
                <span className="text-slate-400 truncate">{e.name}</span>
                <span className="text-white font-mono">{e.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-slate-600">{label}</div>
      <div className={`font-mono text-sm ${color || 'text-white'}`}>{value}</div>
    </div>
  );
}

function FinanceCard({ finance }) {
  if (!finance) return null;
  return (
    <div className="glass-card p-4 border border-white/5">
      <div className="flex items-center gap-2 mb-3">
        <IndianRupee size={14} className="text-amber-400" />
        <span className="text-[10px] font-semibold tracking-widest uppercase text-amber-400">Finance</span>
      </div>
      <div className="space-y-2 text-xs">
        <Row label="Payments received" value={`₹${fmt(finance.payments_total)}`} sub={`${finance.payments_received?.length || 0} invoices`} />
        <Row label="Invoices sent" value={finance.invoices_sent || 0} />
        <Row label="Income entries" value={`₹${fmt(finance.income_total)}`} sub={`${finance.income_entries?.length || 0} entries`} color="text-emerald-400" />
        <Row label="Expense entries" value={`₹${fmt(finance.expense_total)}`} sub={`${finance.expense_entries?.length || 0} entries`} color="text-rose-400" />
        {finance.invoices_overdue?.length > 0 && (
          <div className="mt-2 pt-2 border-t border-white/5">
            <p className="text-[10px] text-rose-400 font-semibold">⚠ {finance.invoices_overdue.length} overdue</p>
            {finance.invoices_overdue.slice(0, 3).map((i, k) => (
              <div key={k} className="text-[11px] text-slate-400">{i.client_name} · ₹{fmt(i.total_amount)} · due {i.due_date}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DeliverablesCard({ deliverables, missed }) {
  if (!deliverables) return null;
  return (
    <div className="glass-card p-4 border border-white/5">
      <div className="flex items-center gap-2 mb-3">
        <FileText size={14} className="text-cyan-400" />
        <span className="text-[10px] font-semibold tracking-widest uppercase text-cyan-400">Deliverables</span>
      </div>
      <div className="space-y-2 text-xs">
        <Row label="Delivered yesterday" value={deliverables.delivered_yesterday || 0} color="text-emerald-400" />
      </div>
      {missed && missed.length > 0 && (
        <div className="mt-2 pt-2 border-t border-white/5">
          <p className="text-[10px] text-rose-400 font-semibold mb-1">Missed deadlines</p>
          {missed.slice(0, 3).map((d, k) => (
            <div key={k} className="text-[11px] text-slate-400">{d.title} · {d.client_name} · was due {d.deadline}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function AssetsCard({ assets }) {
  if (!assets) return null;
  const total = (assets.assigned?.length || 0) + (assets.returned?.length || 0);
  if (total === 0 && !assets.damaged?.length) {
    return (
      <div className="glass-card p-4 border border-white/5">
        <div className="flex items-center gap-2 mb-3">
          <Package size={14} className="text-slate-400" />
          <span className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">Assets</span>
        </div>
        <p className="text-[11px] text-slate-600">No asset activity yesterday</p>
      </div>
    );
  }
  return (
    <div className="glass-card p-4 border border-white/5">
      <div className="flex items-center gap-2 mb-3">
        <Package size={14} className="text-purple-400" />
        <span className="text-[10px] font-semibold tracking-widest uppercase text-purple-400">Assets</span>
      </div>
      <div className="space-y-2 text-xs">
        <Row label="Assigned" value={assets.assigned?.length || 0} />
        <Row label="Returned" value={assets.returned?.length || 0} />
        {assets.damaged?.length > 0 && (
          <Row label="Damaged" value={assets.damaged.length} color="text-rose-400" />
        )}
      </div>
    </div>
  );
}

function Row({ label, value, sub, color }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="text-slate-400">{label}</div>
        {sub && <div className="text-[10px] text-slate-600">{sub}</div>}
      </div>
      <div className={`font-mono ${color || 'text-white'}`}>{value}</div>
    </div>
  );
}

function EmployeePatternCard({ pattern }) {
  const worstSev = pattern.flags?.reduce((w, f) => (f.severity === 'high' ? 'high' : (f.severity === 'medium' && w !== 'high' ? 'medium' : w)), 'low');
  return (
    <div className={`glass-card p-3 border ${SEV_COLOR[worstSev] || SEV_COLOR.low}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm text-white font-medium">{pattern.name}</span>
        <span className="text-[10px] text-slate-500">· {pattern.department}</span>
        <div className="flex-1" />
        <span className="text-[10px] text-slate-500">7d:</span>
        <span className="text-[10px] font-mono text-slate-300">{pattern.stats_7d?.completions || 0} done</span>
        <span className="text-[10px] font-mono text-slate-400">· {pattern.stats_7d?.on_time_pct || 0}% on-time</span>
        {pattern.stats_7d?.overdue_open > 0 && <span className="text-[10px] font-mono text-rose-400">· {pattern.stats_7d.overdue_open} overdue</span>}
      </div>
      {pattern.flags?.length > 0 && (
        <div className="space-y-1 pl-3 border-l border-white/5">
          {pattern.flags.map((f, k) => {
            const Icon = FLAG_ICON[f.kind] || AlertCircle;
            return (
              <div key={k} className="flex items-start gap-2 text-[11px]">
                <Icon size={11} className={`shrink-0 mt-0.5 ${SEV_COLOR[f.severity]?.split(' ')[2] || 'text-slate-400'}`} />
                <span className="text-slate-300">{f.msg}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SchedCard({ icon: Icon, color, title, items, empty, render }) {
  const hasItems = items && items.length > 0;
  return (
    <div className="glass-card p-3 border border-white/5">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={13} className={color} />
        <span className={`text-[10px] font-semibold tracking-widest uppercase ${color}`}>{title}</span>
        {hasItems && <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded bg-white/5 ${color}`}>{items.length}</span>}
      </div>
      {hasItems ? (
        <div className="space-y-1 text-[11px] text-slate-300">
          {items.slice(0, 6).map((it, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <ChevronRight size={10} className="shrink-0 mt-0.5 text-slate-600" />
              {render ? render(it) : <span>{JSON.stringify(it)}</span>}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-slate-600">{empty}</p>
      )}
    </div>
  );
}

function PerformerList({ title, items, format }) {
  return (
    <div className="glass-card p-3 border border-white/5">
      <p className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">{title}</p>
      <div className="space-y-1">
        {(items || []).slice(0, 5).map((e, i) => (
          <div key={i} className="flex items-center justify-between text-[11px]">
            <span className="text-white">{i + 1}. {e.name}</span>
            <span className="text-slate-400 font-mono">{format(e)}</span>
          </div>
        ))}
        {(!items || items.length === 0) && <p className="text-[11px] text-slate-600">Nothing to celebrate yet</p>}
      </div>
    </div>
  );
}

// ---------- Utils ----------

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function fmt(n) {
  if (n == null) return '0';
  return Number(n).toLocaleString('en-IN');
}
